import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role hierarchy for determining access
const ROLE_HIERARCHY = {
  customer: 1,
  contractor: 2,
  support: 3,
  finance: 4,
  ops_dispatch: 5,
  verification_reviewer: 6,
  support_agent: 7,
  admin: 8,
  super_admin: 9
};

// Helper to check if user has admin access
const isAdminRole = (role) => {
  if (!role) return false;
  const normalizedRole = typeof role === 'string' ? role.toLowerCase() : role;
  return ROLE_HIERARCHY[normalizedRole] >= ROLE_HIERARCHY.admin;
};

// Helper to check if user can access admin panel (support level or higher)
const canAccessAdminPanel = (role) => {
  if (!role) return false;
  const normalizedRole = typeof role === 'string' ? role.toLowerCase() : role;
  return ROLE_HIERARCHY[normalizedRole] >= ROLE_HIERARCHY.support;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [roleChangeDetected, setRoleChangeDetected] = useState(false);

  // Refresh user data from server (source of truth)
  const refreshUserFromServer = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return null;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      const response = await axios.get(`${API_URL}/api/auth/me`);
      const freshUser = response.data;
      
      // Always use server data as source of truth
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
      setToken(storedToken);
      
      return freshUser;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      
      // Check for role change / session invalidation
      if (error.response?.status === 401) {
        const isRoleChanged = error.response?.headers?.['x-role-changed'] === 'true' ||
                              error.response?.data?.detail?.includes('permissions have changed');
        
        if (isRoleChanged) {
          setRoleChangeDetected(true);
        }
        
        // Clear invalid session
        logout();
      }
      return null;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken) {
        // ALWAYS refresh user from server on mount
        await refreshUserFromServer();
      }
      
      setLoading(false);
    };

    initAuth();
  }, [refreshUserFromServer]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      // Store fresh data from server
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      setRoleChangeDetected(false);
      
      // Dispatch custom event for WebSocket
      window.dispatchEvent(new CustomEvent('auth:login', { detail: { userId: userData.id } }));
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(newUser);
      
      return { success: true, user: newUser };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const switchRole = async (newRole) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/switch-role`, { role: newRole });
      const { access_token, user: updatedUser } = response.data;
      
      // Update stored data
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(updatedUser);
      
      return { success: true, user: updatedUser };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to switch role'
      };
    }
  };

  const logout = async () => {
    try {
      // Call server logout to invalidate session
      if (token) {
        await axios.post(`${API_URL}/api/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state regardless of server response
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('contractorProfile');
      
      delete axios.defaults.headers.common['Authorization'];
      
      setToken(null);
      setUser(null);
      setRoleChangeDetected(false);
      
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    
    return { success: true };
  };

  // Computed properties for role checking
  const activeRole = user?.active_role || user?.role?.toLowerCase?.() || user?.role || null;
  const primaryRole = user?.primary_role || activeRole;
  const availableRoles = user?.available_roles || (activeRole ? [activeRole] : []);
  const isSuperAdmin = primaryRole === 'super_admin' || user?.role === 'super_admin';
  const isAdmin = isAdminRole(primaryRole);
  const hasAdminAccess = canAccessAdminPanel(primaryRole);
  const isContractor = activeRole === 'contractor';
  const isCustomer = activeRole === 'customer';
  const canSwitchRoles = availableRoles.length > 1;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      signup, 
      logout,
      switchRole,
      refreshUserFromServer,
      // Role helpers
      activeRole,
      primaryRole,
      availableRoles,
      isSuperAdmin,
      isAdmin,
      hasAdminAccess,
      isContractor,
      isCustomer,
      canSwitchRoles,
      // Session state
      roleChangeDetected,
      setRoleChangeDetected
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
